import { execa, type ResultPromise } from 'execa'
import { getBpCli } from './bp-cli.js'
import { BaseCommand } from './base-command.js'

export interface BpAddCommandOptions {
  integration: string
  botPath: string
  workspaceId: string
  credentials: {
    token: string
    apiUrl: string
  }
}

export class BpAddCommand extends BaseCommand<never, void> {
  private childProcess: ResultPromise | null = null
  private killed = false

  constructor(private options: BpAddCommandOptions) {
    super()
  }

  async run(): Promise<void> {
    const { integration, botPath, workspaceId, credentials } = this.options

    // Get bp command path
    const { path: bpCommand } = await getBpCli(botPath)

    const args = [
      'add',
      `integration:${integration}`,
      '-y',
      '--installPath',
      botPath,
      '--token',
      credentials.token,
      '--workspaceId',
      workspaceId,
      '--apiUrl',
      credentials.apiUrl,
    ]

    this.childProcess = execa(bpCommand, args, {
      cwd: botPath,
      stdio: ['ignore', 'pipe', 'pipe'],
    })

    // Capture stdout
    if (this.childProcess.stdout) {
      this.childProcess.stdout.on('data', (data: Buffer) => {
        const text = data.toString()
        this.emit('stdout', text)
      })
    }

    // Capture stderr
    if (this.childProcess.stderr) {
      this.childProcess.stderr.on('data', (data: Buffer) => {
        const text = data.toString()
        this.emit('stderr', text)
      })
    }

    // Handle process exit
    this.childProcess.then(
      () => {
        // Process completed successfully
        this.emit('done', undefined)
      },
      (error: any) => {
        if (this.killed) {
          return
        }

        const errorObj = {
          exitCode: error.exitCode ?? 1,
          stderr: error.stderr || '',
          message: error.message || 'Process exited with error',
        }

        this.emit('error', errorObj)
      }
    )
  }

  kill(signal: NodeJS.Signals = 'SIGTERM'): void {
    this.killed = true
    if (this.childProcess) {
      this.childProcess.kill(signal)
    }
  }
}
